// client/src/dnd/emitter_tool/engine/SubEmitterManager.js
import GravityManager from "./GravityManager.js";
import GlowManager from "./GlowManager.js";
import { createSimpleEmitter } from "../runtime/Runtime_Parts/SimpleEmitter.js";
import { deepMerge, toNum } from "../runtime/Runtime_Parts/utils.js";

function mergeIntoDeep(target, src) {
  if (!src || typeof src !== "object") return target;
  for (const k of Object.keys(src)) {
    const sv = src[k];
    const tv = target[k];
    if (sv && typeof sv === "object" && !Array.isArray(sv)) {
      if (!tv || typeof tv !== "object" || Array.isArray(tv)) target[k] = {};
      mergeIntoDeep(target[k], sv);
    } else {
      target[k] = sv;
    }
  }
  return target;
}

function textureExists(textures, name) {
  if (!name) return false;
  try {
    return !!(textures && Object.prototype.hasOwnProperty.call(textures, name));
  } catch {
    return false;
  }
}

function selectionFromEffectOrOverrides(textures, effect, overrides, fallbackSel) {
  const ov = overrides || {};
  const eff = effect || {};

  if (ov.selectedSprite && textureExists(textures, ov.selectedSprite)) {
    return {
      mode: "single",
      single: ov.selectedSprite,
      multi: [],
      anim: [],
      animFps: toNum(ov.animFps, 12) || 12,
    };
  }
  if (Array.isArray(ov.selectedSprites) && ov.selectedSprites.length) {
    const ok = ov.selectedSprites.filter((n) => textureExists(textures, n));
    if (ok.length === 1)
      return {
        mode: "single",
        single: ok[0],
        multi: [],
        anim: [],
        animFps: toNum(ov.animFps, 12) || 12,
      };
    if (ok.length > 1)
      return {
        mode: "multi",
        single: null,
        multi: ok,
        anim: [],
        animFps: toNum(ov.animFps, 12) || 12,
      };
  }
  if (Array.isArray(ov.selectedAnimFrames) && ov.selectedAnimFrames.length >= 2) {
    const ok = ov.selectedAnimFrames.filter((n) => textureExists(textures, n));
    if (ok.length >= 2)
      return {
        mode: "anim",
        single: null,
        multi: [],
        anim: ok,
        animFps: toNum(ov.animFps, 12) || 12,
      };
  }

  if (eff.selectedSprite && textureExists(textures, eff.selectedSprite)) {
    return {
      mode: "single",
      single: eff.selectedSprite,
      multi: [],
      anim: [],
      animFps: toNum(ov.animFps, 12) || 12,
    };
  }
  if (Array.isArray(eff.selectedSprites) && eff.selectedSprites.length) {
    const ok = eff.selectedSprites.filter((n) => textureExists(textures, n));
    if (ok.length === 1)
      return {
        mode: "single",
        single: ok[0],
        multi: [],
        anim: [],
        animFps: toNum(ov.animFps, 12) || 12,
      };
    if (ok.length > 1)
      return {
        mode: "multi",
        single: null,
        multi: ok,
        anim: [],
        animFps: toNum(ov.animFps, 12) || 12,
      };
  }
  if (Array.isArray(eff.selectedAnimFrames) && eff.selectedAnimFrames.length >= 2) {
    const ok = eff.selectedAnimFrames.filter((n) => textureExists(textures, n));
    if (ok.length >= 2)
      return {
        mode: "anim",
        single: null,
        multi: [],
        anim: ok,
        animFps: toNum(eff.animFps, 12) || 12,
      };
  }

  return fallbackSel;
}

const GLOW_KEYS = new Set([
  "glowEnabled",
  "glowPercent",
  "glowIntensity",
  "glowReachM",
  "glowReachPx",
  "glowDurationS",
  "tintEnabled",
  "tintStart",
  "tintEnd",
]);

function mergeGlowKeys(dst, src) {
  if (!src) return;
  for (const k of Object.keys(src)) {
    if (GLOW_KEYS.has(k)) dst[k] = src[k];
  }
}

function toDelayMs(input) {
  if (typeof input === "string") {
    const s = input.trim().toLowerCase();
    if (s.endsWith("s")) {
      const v = Number.parseFloat(s.slice(0, -1));
      return Number.isFinite(v) && v >= 0 ? Math.floor(v * 1000) : 0;
    }
    const n = Number(s);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
  }
  const v = Number(input);
  return Number.isFinite(v) && v >= 0 ? Math.floor(v) : 0;
}

function normalizeStartMode(v) {
  const s = String(v || "").toLowerCase();
  if (s === "delay" || s === "wait" || s === "waitms" || s === "wait_ms")
    return "delay";
  if (
    s === "afterparent" ||
    s === "after_parent" ||
    s === "after-parent" ||
    s === "after" ||
    s === "end"
  )
    return "afterParent";
  if (s === "immediate" || s === "now" || s === "instant") return "immediate";
  return "immediate";
}

function normalizeRunForMode(v) {
  const s = String(v || "").toLowerCase();
  if (s === "timed" || s === "time" || s === "duration") return "timed";
  return "inherit";
}

export function createSubEmitterManager(opts = {}) {
  const {
    PIXI,
    appRef,
    parentLayerMount,
    aboveLayerMount,
    baseSpriteSource,
    baseSelection,
    atlasTextures,
    imageRect,
    collision,
    pxPerMeter,
    parentEmitterShape,
    layers,
  } = opts;

  const childEmitters = [];
  const childGravities = [];
  const childGlows = [];
  const childESArr = [];
  const childShapes = [];
  const childShapeSettings = [];
  const emitterGroups = [];
  const childState = [];
  const childEventUnsubs = [];
  let lastParentAlive = false;

  function makeGroup(mount, name, z) {
    const g = new PIXI.Container();
    try {
      g.sortableChildren = true;
    } catch {}
    if (Number.isFinite(z)) {
      try {
        g.zIndex = z;
      } catch {}
    }
    try {
      g.name = name;
    } catch {}
    try {
      mount.addChild(g);
    } catch {}
    emitterGroups.push(g);
    return g;
  }

  (layers || []).forEach((L, idx) => {
    const layerShape = L.emitterShape ?? parentEmitterShape;
    const layerShapeSettings = mergeIntoDeep({}, L.shapeSettings || {});
    const layerES = mergeIntoDeep({}, L.effectSettings || {});
    if (L.overrides && L.overrides.effectSettings) {
      mergeIntoDeep(layerES, L.overrides.effectSettings);
    }

    if (L.__selectionOverrides) {
      const ov = L.__selectionOverrides;
      if (ov.selectedSprite) layerES.selectedSprite = ov.selectedSprite;
      if (Array.isArray(ov.selectedSprites))
        layerES.selectedSprites = ov.selectedSprites.slice();
      if (Array.isArray(ov.selectedAnimFrames))
        layerES.selectedAnimFrames = ov.selectedAnimFrames.slice();
      if (ov.animFps != null)
        layerES.animFps = toNum(ov.animFps, layerES.animFps ?? 12) || 12;
    }

    childESArr.push(layerES);
    childShapes.push(layerShape);
    childShapeSettings.push(layerShapeSettings);

    const cg = new GravityManager();
    const mount = L.aboveParent === false ? parentLayerMount : aboveLayerMount;
    const childSel = selectionFromEffectOrOverrides(
      atlasTextures,
      layerES,
      L.__selectionOverrides,
      baseSelection
    );

    const z = (L.aboveParent === false ? 1200 : 2200) + idx;
    const childGroup = makeGroup(mount, `emitter:child:${L.id || idx}`, z);

    const childId = L.id || String(idx);
    const configEventName = `EmitterTool:GlowConfig:child:${childId}`;

    const gm = new GlowManager({ app: appRef, pxPerMeter, configEventName });
    gm.configure(layerES, { pxPerMeter });

    const handler = (ev) => {
      const detail = ev?.detail || {};
      mergeGlowKeys(layerES, detail);
      try {
        gm.configure(layerES, { pxPerMeter });
      } catch {}
    };
    try {
      window.addEventListener(configEventName, handler);
      childEventUnsubs.push(() => {
        try {
          window.removeEventListener(configEventName, handler);
        } catch {}
      });
    } catch {}

    const em = createSimpleEmitter(appRef, childGroup, {
      textures: atlasTextures || {},
      pxPerMeter,
      emitterShape: layerShape,
      shapeSettings: layerShapeSettings,
      effectSettings: layerES,
      spriteSource: baseSpriteSource,
      selection: childSel,
      imageRect,
      collision,
      glowManager: gm,
      gravityManager: cg,
    });

    try {
      em.setSpawning(false);
    } catch {}

    childEmitters.push(em);
    childGravities.push(cg);
    childGlows.push(gm);

    const startModeNorm = normalizeStartMode(L.startMode || "immediate");
    const runForModeNorm = normalizeRunForMode(L.runForMode || "inherit");

    childState.push({
      id: childId,

      startMode: startModeNorm,
      delayMs: toDelayMs(L.delayMs),
      runForMode: runForModeNorm,
      runDurationS: Number(L.runDurationS) || 0.5,

      armed: false,
      live: false,
      spawnEnabled: false,
      fullyDone: false,
      startedAtMs: 0,
      killedAtMs: 0,

      originUsed: { x: 0, y: 0 },
      parentLinked: true,

      startAtMs: 0,
      waitingForParentEnd: false,
    });
  });

  function anyChildGravityEnabled() {
    return childESArr.some((es) => es?.gravityEnabled);
  }

  function _configureOneGravity(i, origin) {
    try {
      childGravities[i].configure({
        emitterShape: childEmitters[i].getEmitterShape?.() ?? childShapes[i],
        shapeSettings:
          childEmitters[i].getShapeSettings?.() ?? childShapeSettings[i] ?? {},
        effectSettings: childESArr[i],
        origin,
        pxPerMeter,
        imageRect,
      });
    } catch {}
  }

  function _configureOneGlow(i) {
    try {
      childGlows[i].setPxPerMeter(pxPerMeter);
      childGlows[i].configure(childESArr[i], { pxPerMeter });
    } catch {}
  }

  function configureGravityChildren(origin) {
    for (let i = 0; i < childEmitters.length; i++) {
      _configureOneGravity(i, origin);
    }
  }

  function setOriginChildren(p) {
    for (let i = 0; i < childEmitters.length; i++) {
      const em = childEmitters[i];
      try {
        em.setOrigin(p);
      } catch {}
    }
    for (let i = 0; i < childState.length; i++) {
      childState[i].originUsed = { x: p.x, y: p.y };
    }
  }

  function setRotationDegChildren(deg) {
    for (let i = 0; i < childEmitters.length; i++) {
      const em = childEmitters[i];
      try {
        em.setRotationDeg(deg);
      } catch {}
    }
  }

  function _prime(i, frames = 2) {
    const dt = 1 / 60;
    for (let k = 0; k < frames; k++) {
      try {
        childGravities[i].update(dt);
      } catch {}
      try {
        childEmitters[i].update(dt);
      } catch {}
    }
  }

  function _resetLiveState(st) {
    st.armed = false;
    st.live = false;
    st.spawnEnabled = false;
    st.fullyDone = false;
    st.startedAtMs = 0;
    st.killedAtMs = 0;
    st.parentLinked = true;
    st.startAtMs = 0;
    st.waitingForParentEnd = false;
  }

  function _setupEmitterConfig(i, origin) {
    const em = childEmitters[i];
    const es = childESArr[i];
    const shape = childShapes[i];
    const shapeSettings = childShapeSettings[i] || {};

    try {
      em.reset?.({ clearParticles: true });
    } catch {}

    if (em.setEmitterShape) {
      try {
        em.setEmitterShape(shape);
      } catch {}
    }
    if (em.setShapeSettings) {
      try {
        em.setShapeSettings(shapeSettings);
      } catch {}
    }
    if (em.setEffectSettings) {
      try {
        em.setEffectSettings(es);
      } catch {}
    }

    try {
      em.setOrigin(origin);
    } catch {}
    _configureOneGravity(i, origin);
    _configureOneGlow(i);
  }

  function _startChildNow(i, st, tNowMs) {
    const em = childEmitters[i];

    st.live = true;
    st.spawnEnabled = true;
    st.startedAtMs = tNowMs;
    st.killedAtMs = 0;
    st.armed = false;
    st.waitingForParentEnd = false;
    st.parentLinked = st.runForMode === "inherit";

    _setupEmitterConfig(i, st.originUsed);

    try {
      em.setSpawning(true);
    } catch {}

    _prime(i, 2);
  }

  function resetChildren(opts) {
    const arg = opts || {};
    for (let i = 0; i < childEmitters.length; i++) {
      const em = childEmitters[i];
      em.reset(arg);
      try {
        em.setSpawning(false);
      } catch {}
    }
    for (let i = 0; i < childState.length; i++) {
      _resetLiveState(childState[i]);
    }
  }

  function applyLatestPatches(particlesPatchStore, layersRef) {
    if (!particlesPatchStore || !layersRef) return;
    for (let i = 0; i < layersRef.length; i++) {
      const id = layersRef[i]?.id;
      if (!id) continue;
      const patch = particlesPatchStore.children.get(id);
      if (patch) mergeIntoDeep(childESArr[i], patch);
      _configureOneGlow(i);
    }
  }

  function _applyRuleToIndex(i, ruleObj) {
    if (i < 0 || i >= childState.length) return;
    const st = childState[i];

    const convertedDelayMs = toDelayMs(ruleObj.delayMs);

    st.startMode = normalizeStartMode(
      ruleObj.startMode || st.startMode || "immediate"
    );
    st.delayMs = convertedDelayMs;
    st.runForMode = normalizeRunForMode(
      ruleObj.runForMode || st.runForMode || "inherit"
    );
    st.runDurationS = Number(ruleObj.runDurationS) || st.runDurationS || 0.5;
  }

  function applyChildRuleOverrideByIdOrIndex(childId, orderIndex, ruleObj) {
    let foundIndex = childState.findIndex((s) => s.id === childId);
    if (foundIndex < 0 && Number.isInteger(orderIndex)) {
      foundIndex = orderIndex;
    }
    if (foundIndex >= 0) _applyRuleToIndex(foundIndex, ruleObj || {});
  }

  function armChildrenStart(origin, tNowMs) {
    for (let i = 0; i < childEmitters.length; i++) {
      const em = childEmitters[i];
      const st = childState[i];

      _resetLiveState(st);

      st.originUsed = { x: origin.x, y: origin.y };

      const mode = st.startMode || "immediate";

      if (mode === "afterParent") {
        st.waitingForParentEnd = true;
        st.armed = true;
        try {
          em.setSpawning(false);
        } catch {}
        continue;
      }

      st.armed = true;
      st.startAtMs =
        mode === "delay"
          ? tNowMs + Math.max(0, st.delayMs)
          : tNowMs;

      try {
        em.setSpawning(false);
      } catch {}
    }
  }

  function _onParentFinished(tNowMs, finalOrigin) {
    for (let i = 0; i < childEmitters.length; i++) {
      const em = childEmitters[i];
      const st = childState[i];
      if (!st.waitingForParentEnd || st.fullyDone || st.live) continue;

      if (
        finalOrigin &&
        Number.isFinite(finalOrigin.x) &&
        Number.isFinite(finalOrigin.y)
      ) {
        st.originUsed = { x: finalOrigin.x, y: finalOrigin.y };
      }

      st.waitingForParentEnd = false;
      st.armed = true;
      st.startAtMs = tNowMs + (st.delayMs > 0 ? st.delayMs : 0);
      st.spawnEnabled = false;

      try {
        em.setSpawning(false);
      } catch {}
    }
  }

  function armAfterParent(finalOrigin, tNowMs) {
    _onParentFinished(tNowMs, finalOrigin);
  }

  function tickChildren(
    dt,
    tNowMs,
    parentAlive,
    parentSpawning,
    resolvePxPerMeterFn,
    applyOrbitToEmitter,
    applyRotationToEmitter,
    PIXIRef,
    parentOrigin
  ) {
    const parentOn = !!(parentAlive || parentSpawning);

    if (lastParentAlive && !parentAlive) {
      _onParentFinished(tNowMs, parentOrigin);
    }
    lastParentAlive = !!parentAlive;

    for (let i = 0; i < childEmitters.length; i++) {
      const em = childEmitters[i];
      const st = childState[i];
      if (st.fullyDone) continue;

      _configureOneGlow(i);

      if (st.armed && !st.live) {
        if (
          parentOn &&
          parentOrigin &&
          Number.isFinite(parentOrigin.x) &&
          Number.isFinite(parentOrigin.y)
        ) {
          st.originUsed.x = parentOrigin.x;
          st.originUsed.y = parentOrigin.y;
        }

        const isAfterParent = st.startMode === "afterParent";

        if (!isAfterParent) {
          if (tNowMs >= st.startAtMs) {
            _startChildNow(i, st, tNowMs);
          }
        } else {
          if (!parentAlive && st.startAtMs > 0 && tNowMs >= st.startAtMs) {
            _startChildNow(i, st, tNowMs);
          }
        }
      }

      if (!st.live) continue;

      if (st.runForMode === "inherit") {
        if (!parentOn) {
          st.spawnEnabled = false;
          try {
            em.setSpawning(false);
          } catch {}
          if (!st.killedAtMs) st.killedAtMs = tNowMs;
        }
      } else if (st.runForMode === "timed") {
        const lived = (tNowMs - st.startedAtMs) / 1000;
        if (lived >= st.runDurationS) {
          st.spawnEnabled = false;
          try {
            em.setSpawning(false);
          } catch {}
          if (!st.killedAtMs) st.killedAtMs = tNowMs;
        }
      }

      try {
        childGravities[i].update(dt);
      } catch {}
      try {
        em.update(dt);
      } catch {}

      try {
        applyOrbitToEmitter(
          em,
          childESArr[i],
          resolvePxPerMeterFn(80),
          dt,
          PIXIRef
        );
      } catch {}
      try {
        applyRotationToEmitter(em, childESArr[i], dt);
      } catch {}

      if (!st.spawnEnabled) {
        const stillLive = em.hasLiveParticles && em.hasLiveParticles();
        if (!stillLive) {
          st.live = false;
          st.fullyDone = true;
        }
      }
    }
  }

  function setSpawningChildren(on) {
    const now =
      typeof performance !== "undefined" && performance.now
        ? performance.now()
        : Date.now();

    for (let i = 0; i < childEmitters.length; i++) {
      const st = childState[i];
      const em = childEmitters[i];

      st.parentLinked = !!on && st.runForMode === "inherit";

      if (!on && st.runForMode === "inherit" && st.live) {
        st.spawnEnabled = false;
        try {
          em.setSpawning(false);
        } catch {}
        if (!st.killedAtMs) {
          st.killedAtMs = now;
        }
      } else if (st.live) {
        try {
          em.setSpawning(st.spawnEnabled);
        } catch {}
      }
    }
  }

  function getChildEmitters() {
    return childEmitters.slice();
  }

  function getChildESArr() {
    return childESArr;
  }

  function areChildrenAllFinished() {
    return !childState.some((st) => !st.fullyDone);
  }

  function destroy() {
    for (let i = 0; i < childEmitters.length; i++) {
      const em = childEmitters[i];
      try {
        em.destroy?.();
      } catch {}
    }
    for (let i = 0; i < childGlows.length; i++) {
      const gm = childGlows[i];
      try {
        gm.destroy?.();
      } catch {}
    }
    for (let i = 0; i < childEventUnsubs.length; i++) {
      const unsub = childEventUnsubs[i];
      try {
        unsub();
      } catch {}
    }
    for (let i = 0; i < emitterGroups.length; i++) {
      const g = emitterGroups[i];
      try {
        g.removeChildren();
      } catch {}
      try {
        g.parent && g.parent.removeChild(g);
      } catch {}
      try {
        g.destroy({ children: true });
      } catch {}
    }
  }

  try {
    window.addEventListener("EmitterTool:SubEmitterRule", (e) => {
      const d = e?.detail || {};
      const cid = d.id;
      const ord = Number.isInteger(d.orderIndex) ? d.orderIndex : undefined;
      applyChildRuleOverrideByIdOrIndex(cid, ord, d);
    });
  } catch {}

  function applyChildRuleOverride(childId, ruleObj) {
    applyChildRuleOverrideByIdOrIndex(childId, undefined, ruleObj || {});
  }

  return {
    armChildrenStart,
    armAfterParent,
    tickChildren,
    areChildrenAllFinished,
    getChildEmitters,
    getChildESArr,
    anyChildGravityEnabled,
    setOriginChildren,
    setSpawningChildren,
    resetChildren,
    setRotationDegChildren,
    configureGravityChildren,
    applyLatestPatches,
    applyChildRuleOverride,
    destroy,
  };
}

export default { createSubEmitterManager };
